<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import CartWidget from '~/components/cart/CartWidget.vue'
import Discounts from '~/components/cart/Discounts.vue'
import Total from '~/components/cart/Total.vue'
import BasicButton from '~/components/form-elements/BasicButton.vue'
import YesNoSwitch from '~/components/form-elements/YesNoSwitch.vue'
import Auth from '~/components/modal-windows/Auth.vue'
import constants from '~/constants'
import AuthAction from '~/enums/auth-action'
import { OrderPaymentMethod } from '~/enums/order-payment-method'
import Page from '~/enums/page'
import UserPermission from '~/enums/user-permission'
import helpers from '~/helpers'
import formatter from '~/helpers/formatter'
import { useAuthStore } from '~/store/auth'
import { useCartStore } from '~/store/cart'
import { useUserStore } from '~/store/user'

const props = defineProps<{
  page: Page
}>()

const authStore = useAuthStore()
const cartStore = useCartStore()
const userStore = useUserStore()

const isCheckout = computed(() => props.page === Page.Checkout)

async function openAuthModalWindow (): Promise<void> {
  await useModal({ component: Auth, attrs: { action: AuthAction.SignIn } }).open()
}
</script>

<template>
  <aside class="relative select-none rounded-[20px] bg-white p-6 md:sticky md:top-6">
    <template v-if="!isCheckout">
      <Discounts />

      <div class="my-4 h-px w-full bg-black opacity-[.05]" />
    </template>

    <Total :is-checkout="isCheckout" />

<!--    <CartWidget-->
<!--      v-if="isCheckout"-->
<!--      class="mt-4 w-full bg-[rgba(89,173,59,.1)]"-->
<!--    >-->
<!--      <div class="flex items-start">-->
<!--        <YesNoSwitch-->
<!--          v-model="cartStore.form.isAgreeToTermsAndConditions"-->
<!--          class="mt-1"-->
<!--          :no-text="null"-->
<!--          :yes-text="null"-->
<!--        />-->

<!--        <div class="ml-1 text-sm leading-[140%] text-[#434242]">-->
<!--          <template v-if="cartStore.orderPaymentMethod === OrderPaymentMethod.Dolyame">-->
<!--            Я&nbsp;принимаю условия >, <a href="https://static.dolyame.ru/static/dolyame/dolyame_contract.pdf" target="_blank">договора «Долями»</a> и&nbsp;даю своё согласие на&nbsp;<a :href="constants.documentUrls.dataProtectionPolicy" target="_blank">обработку персональных данных</a>.-->
<!--          </template>-->
<!--          <template v-else>-->
<!--            Я&nbsp;принимаю условия <a :href="constants.documentUrls.userAgreement" target="_blank">пользовательского соглашения</a> и&nbsp;даю своё <a :href="constants.documentUrls.dataProtectionPolicy" target="_blank">согласие на&nbsp;обработку персональных данных</a>.-->
<!--          </template>-->
<!--        </div>-->
<!--      </div>-->
<!--    </CartWidget>-->

    <CartWidget
      v-if="cartStore.preOrderCartProductsData.length"
      class="mt-4 w-full bg-[rgba(150,89,173,.1)]"
    >
      <div class="flex items-start">
        <YesNoSwitch
          v-model="cartStore.form.isAgreeToPreOrder"
          class="mt-1"
          :no-text="null"
          :yes-text="null"
        />

        <div class="ml-1 text-sm leading-[140%] text-[#434242]">
          <template v-if="cartStore.preOrderCartProductsData.length === 1">
            Даю своё согласие на&nbsp;отправку всего заказа после поступления <a :href="helpers.getProductPath(cartStore.preOrderCartProductsData[0])" target="_blank">товара по&nbsp;предзаказу</a> на&nbsp;склад.
          </template>
          <template v-else>
            Даю своё согласие на&nbsp;отправку всего заказа после поступления товаров по&nbsp;предзаказу на&nbsp;склад.
          </template>
        </div>
      </div>
    </CartWidget>

    <CartWidget
      v-if="isCheckout && userStore.hasPermission(UserPermission.CreateBloggerOrder)"
      class="mt-4 w-full bg-[rgba(173,59,89,.1)]"
    >
      <div class="flex items-center">
        <YesNoSwitch
          v-model="cartStore.isBloggerOrder"
          class="[&_*]:!text-[13px] [&_*]:!font-normal"
          :no-text="null"
          :yes-text="null"
        />

        <div class="ml-1 text-sm leading-[140%] text-[#434242]">
          Блогерский заказ
        </div>
      </div>
    </CartWidget>

    <CartWidget
      v-if="isCheckout && userStore.hasPermission(UserPermission.CreateRepeatOrder)"
      class="mt-4 w-full bg-[rgba(59,89,173,.1)]"
    >
      <div class="flex items-center">
        <YesNoSwitch
          v-model="cartStore.isRepeatOrder"
          class="[&_*]:!text-[13px] [&_*]:!font-normal"
          :no-text="null"
          :yes-text="null"
        />

        <div class="ml-1 text-sm leading-[140%] text-[#434242]">
          Повторный заказ
        </div>
      </div>
    </CartWidget>

    <CartWidget
      v-if="isCheckout && cartStore.orderPaymentMethod === OrderPaymentMethod.CloudPayments"
      class="mt-4 w-full bg-[rgba(89,173,59,.1)]"
    >
      <div class="flex items-start">
        <YesNoSwitch
          v-model="cartStore.useSbpPayment"
          class="mt-1 [&_*]:!text-[13px] [&_*]:!font-normal"
          :no-text="null"
          :yes-text="null"
        />

        <div class="ml-1 text-sm leading-[140%] text-[#434242]">
          Оплатить через СБП<br>
          Доставка до ПВЗ будет бесплатной
        </div>
      </div>
    </CartWidget>

    <Notification
      v-if="cartStore.useSbpPayment && cartStore.totalInKopecks < 1000"
      class="mt-4"
      is-small
      status="warning"
    >
      Минимальная сумма заказа при оплате через СБП&nbsp;— 10 ₽.
    </Notification>

    <BasicButton
      v-if="isCheckout"
      class="mt-4 !h-[56px]"
      color="green"
      :is-loading="cartStore.isOrderCreating"
      title="Оплатить заказ"
      @click="cartStore.payForOrder"
    />

    <div class="my-4 h-px w-full bg-black opacity-[.05]" />

    <div v-if="isCheckout" class="flex items-end">
      <p class="mt-4 !text-xs leading-[140%] text-[#434242] [&>a]:!text-main-green">
        Оплачивая, вы&nbsp;даете согласие на&nbsp;<a :href="constants.documentUrls.dataProtectionPolicy" target="_blank">обработку персональных данных</a> и&nbsp;принимаете условия <a :href="constants.documentUrls.userAgreement" target="_blank">пользовательского соглашения</a>
      </p>

      <YesNoSwitch
        v-model="cartStore.form.isAgreeToTermsAndConditions"
        class="mb-3"
        :no-text="null"
        :yes-text="null"
      />
    </div>

    <div v-if="!isCheckout" class="flex flex-col sm:flex-row sm:items-stretch">
      <CartWidget class="w-full bg-[rgba(254,197,67,.15)]">
        <p v-if="false" class="text-[13px] leading-tight text-[#434242]">
          В&nbsp;связи с&nbsp;высоким потоком заказов время обработки увеличено до&nbsp;3&nbsp;дней.
        </p>

        <div class="flex items-center justify-between">
          <span class="text-[13px] leading-tight text-[#434242]">
            Срочная сборка
          </span>

          <div class="flex items-center">
            <div v-if="cartStore.isUrgentOrder" class="text-[13px] font-medium leading-snug text-main-green">
              {{ formatter.formatPrice(cartStore.urgentOrderPriceInKopecks, true) }}&nbsp;₽
            </div>

            <YesNoSwitch
              v-model="cartStore.isUrgentOrder"
              class="[&_*]:!text-[13px] [&_*]:!font-normal"
              :no-text="null"
              :yes-text="null"
            />
          </div>
        </div>

        <div v-if="cartStore.isUrgentOrder" class="mt-3 border-t border-[rgba(0,0,0,.05)] pt-3">
          <p class="text-[13px] leading-[1.4] text-[#434242] opacity-[.4]">
            Спасибо,&nbsp;обработаем ваш&nbsp;заказ с&nbsp;самым высоким приоритетом!
          </p>
        </div>
      </CartWidget>
    </div>

    <CartWidget
      v-if="!isCheckout && !authStore.isUser && cartStore.cashbackInPoints"
      class="mt-4 w-full bg-main-very-light-gray text-sm text-[#434242]"
    >
      <div>
        <a class="inline text-main-green" href="#" @click.prevent="openAuthModalWindow">
          Войдите
        </a>
        <span>, чтобы получить {{ cartStore.cashbackInPoints }}&nbsp;баллов iTAB&nbsp;Плюс за&nbsp;покупку без&nbsp;промокодов и&nbsp;баллов.</span>
      </div>
    </CartWidget>
  </aside>
</template>
